import React, { useEffect, useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

export default function ClubHeader({ canEdit }) {
  const [clubName, setClubName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const reload = async () => {
    try {
      const { data } = await api.get("/clubs/me");
      setClubName(data?.name || "");
      setLogoUrl(data?.logo_data_url || "");
    } catch (_) {
      // ignore - header simply shows nothing if club lookup fails
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // Listen for cross-component logo updates (Settings page fires this
    // after successful upload/delete so the header refreshes without
    // needing a full page reload).
    const handler = () => reload();
    window.addEventListener("ccs:club-updated", handler);
    return () => window.removeEventListener("ccs:club-updated", handler);
  }, []);

  const startEdit = () => {
    setDraft(clubName);
    setError("");
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError("");
  };

  const save = async () => {
    const name = draft.trim();
    if (!name) {
      setError("Club name cannot be empty");
      return;
    }
    if (name === clubName) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data } = await api.patch("/clubs/me", { name });
      setClubName(data?.name || name);
      setLogoUrl(data?.logo_data_url || "");
      setEditing(false);
      window.dispatchEvent(new CustomEvent("ccs:club-updated"));
    } catch (ex) {
      setError(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <div
      className="w-full flex items-center gap-3 px-4 md:px-6 py-2 md:py-3"
      style={{ background: "#11061d", borderBottom: "1px solid #1f1f1f" }}
      data-testid="club-header"
    >
      {logoUrl && (
        <img
          src={logoUrl}
          alt="Club logo"
          className="h-8 w-8 object-contain rounded"
          data-testid="club-logo"
        />
      )}
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400 hidden md:block">
        Camera Club
      </div>
      {editing ? (
        <div className="flex items-center gap-2 flex-1">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") cancel();
            }}
            autoFocus
            disabled={saving}
            maxLength={120}
            className="bg-neutral-900 border border-neutral-700 px-3 py-1 text-base text-white focus:outline-none focus:border-amber-400"
            style={{ minWidth: 280 }}
            data-testid="club-name-edit-input"
          />
          <button
            onClick={save}
            disabled={saving}
            className="p-2 text-green-400 hover:bg-neutral-800"
            title="Save"
            data-testid="club-name-save"
          >
            <Check size={16} />
          </button>
          <button
            onClick={cancel}
            disabled={saving}
            className="p-2 text-red-400 hover:bg-neutral-800"
            title="Cancel"
            data-testid="club-name-cancel"
          >
            <X size={16} />
          </button>
          {error && (
            <span className="text-xs danger-text" data-testid="club-name-error">
              {error}
            </span>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-1">
          <div
            className="text-lg font-semibold tracking-tight"
            data-testid="club-name-display"
          >
            {clubName || "(no club name)"}
          </div>
          {canEdit && (
            <button
              onClick={startEdit}
              className="p-1.5 muted-text hover:text-white hover:bg-neutral-800"
              title="Edit club name"
              data-testid="club-name-edit-button"
            >
              <Pencil size={14} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}
